import React, {useEffect, useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { statuses, useJobs } from '../../Data/Jobs';
import { PageLoading } from '../../Components/Loading';
import Dropdown from '../../Components/Dropdown';
import Services from '../../Components/Formatting/Services';
import Title from '../../Components/Formatting/Customer/Title';
import Subtitle from '../../Components/Formatting/Customer/Subtitle';
import SearchWrapper, {useSearch} from '../../Components/Search';
import Search from './Search';
import Badge from '../../Components/Badge/Badge';
import { classNames, getEntityLink } from '../../Services/helpers';
import ScheduleJob from '../Customer/ScheduleJob';
import MarkAsDone from '../Customer/MarkAsDone';
import EditJob from '../Customer/EditJob';
import { useDialogues } from '../../Components/Dialogues';
import NoItems from '../../Components/EmptyStates/NoItems';
import Skip from '../Customer/Skip';
import DateTime from '../../Components/Formatting/DateTime';
import Selectable, { SelectBox, useSelectable, SelectableToggle } from '../../Components/Selectable';
import { Transition } from '@headlessui/react'
import MassActions from './MassActions';

const Jobs = () => {

    return <>
        <Selectable>
            <TopBar title="Jobs" right={<SelectableToggle />} />
            <Page>
                <SearchWrapper persist="jobs-search">
                    <Inner  />
                </SearchWrapper>
            </Page>
        </Selectable>
    </>
}

const Inner = () => {
    const {searchDebounced, hasSearchConditions, liveSearch} = useSearch();
    const {jobs, isLoading, refresh, deleteJob} = useJobs(searchDebounced);
    const [scheduling, setScheduling] = useState(null);
    const [markingAsDone, setMarkingAsDone] = useState(null);
    const [skipping, setSkipping] = useState(null);
    const [editing, setEditing] = useState(null);
    const {replaceSelectable} = useSelectable();

    useEffect(() => {
        if (jobs) {
            replaceSelectable(jobs.map(job => `${job.customerId}-${job.id}`));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs])

    return <>
        <Search isLoading={isLoading} refresh={refresh} />

        <MassActions refresh={refresh} className={'mt-4'} sticky />

        <ul className="divide-y divide-gray-200">
            <NoJobs jobs={jobs} isLoading={isLoading} hasSearchConditions={hasSearchConditions} />
            {jobs && jobs.filter(liveSearch).map(job => <Job key={job.id} {...{job, setScheduling, setMarkingAsDone, setSkipping, setEditing, deleteJob, refresh}} />)}
        </ul>

        <ScheduleJob job={scheduling} onClose={() => setScheduling(null)} then={refresh} />
        <MarkAsDone job={markingAsDone} onClose={() => setMarkingAsDone(null)} then={refresh} />
        <Skip job={skipping} onClose={() => setSkipping(null)} then={refresh} />
        <EditJob job={editing} onClose={() => setEditing(null)} then={refresh} />
    </>
}

const NoJobs = ({jobs, isLoading, hasSearchConditions}) => {
    if (!jobs) {
        return <PageLoading />
    }

    if (jobs.length > 0 || isLoading) {
        return null;
    }

    if (hasSearchConditions) {
        return <p className="text-center text-gray-500 text-sm mt-8">
            No jobs match your current search
        </p>
    } else {
        return <NoItems className="my-6" icon="Truck" title="No jobs" message="Add jobs to a customer using the Manage tab" disabled />
    }
}

const Job = ({job, setScheduling, setMarkingAsDone, setSkipping, setEditing, deleteJob, refresh}) => {
    const {confirm} = useDialogues();
    const {isSelecting, toggleSelected} = useSelectable();

    // Clicking the full row should toggle, but we don't want to click the checkbox/dropdown area when not selecting
    const onClick = (isFullRowClick) => {
        if (isSelecting && isFullRowClick) {
            toggleSelected(`${job.customerId}-${job.id}`);
        } else if (isFullRowClick === false && isSelecting === false) {
            setScheduling(job);
        }
    }

    return <li className="flex items-center justify-between py-5" onClick={() => onClick(true)}>
        <Transition
            show={isSelecting}
            enter="transition-[max-width] transition-opacity duration-150"
            enterFrom="opacity-0 max-w-[0px]"
            enterTo="opacity-100 max-w-[100px]"
            leave="transition-[max-width] transition-opacity duration-150"
            leaveFrom="opacity-100 max-w-[100px]"
            leaveTo="opacity-0 max-w-[0px]"
        >
            <div className={"flex flex-none items-center px-4"}>
                <SelectBox id={`${job.customerId}-${job.id}`} />
            </div>
        </Transition>
        <div className="grow" onClick={() => onClick(false)}>
            <p className="text-sm leading-6 text-gray-900 font-semibold">
                {job.title ?? '#'+job.shortId}
                <Badge className={classNames(statuses[job.status].bgColor, statuses[job.status].contrastTextColor, 'ml-4')}
                    title={job.status}
                />
            </p>
            <p className="py-2">
                <span className="font-bold"><Title customer={job.customer} /></span><br />
                <Subtitle customer={job.customer} />
            </p>
            <p>{job.title ?? <Services services={job.services} />}</p>
            <p className="mt-2 text-muted"><DateTime date={job.scheduledDate} hideTime blank="Not scheduled" /></p>
        </div>
        <Transition
            show={!isSelecting}
            enter="transition-[max-width] transition-opacity duration-150"
            enterFrom="opacity-0 max-w-[0px]"
            enterTo="opacity-100 max-w-[100px]"
            leave="transition-[max-width] transition-opacity duration-150"
            leaveFrom="opacity-100 max-w-[100px]"
            leaveTo="opacity-0 max-w-[0px]"
        >
            <div className="flex flex-none items-center px-4">
                <Dropdown
                    disabled={isSelecting}
                    options={[
                        {title: 'View customer', icon: 'User', to: getEntityLink('Customers/Customer', job.customer.id)},
                        {div: true},
                        {title: 'Schedule job', icon: 'CalendarPlus', onClick: () => setScheduling(job)},
                        {title: 'Mark as done', icon: 'Check', onClick: () => setMarkingAsDone(job)},
                        {title: 'Skip job', icon: 'Forward', onClick: () => setSkipping(job)},
                        {div: true},
                        {title: 'Edit job', icon: 'Pencil', onClick: () => setEditing(job)},
                        {title: 'Delete job', icon: 'Trash', onClick: () => confirm(
                            {
                                theme: 'danger',
                                title: 'Delete job #'+job.shortId,
                                confirmLabel: 'Delete',
                                message: 'Are you sure you want to delete this job? This cannot be undone.',
                            },
                            () => deleteJob(job).then(refresh),
                        )},
                    ]}
                />
            </div>
        </Transition>
    </li>
}

export default Jobs;
